import { format } from "date-fns";
import { useContext, useState } from "react";
import { useToaster, DatePicker, Notification } from "rsuite";
import { isCompleteDate } from "../../../../formcontrols/datePickerValue";
import { dateQueries } from "../../../../../config/filter/CustomFilterConfig";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaSlutDatoFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { AccordionFilterItem } from "../../../../accordion/Accordion";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../../filter/ComboBox";
import WarningIcon from "../../../../icons/WarningIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "slutdato";

export default function FirmaSlutDatoFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find active settings
  const settings: FirmaSlutDatoFilterSetting | undefined = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // react things
  const [selectedEndDateQuery, setSelectedEndDateQuery] = useState(
    dateQueries[0],
  );
  const [isEndDateBetween, setIsEndDateBetween] = useState<boolean>(false);
  const [selectedEndDateFrom, setSelectedEndDateFrom] = useState<Date | null>(
    new Date(),
  );
  const [selectedEndDateTo, setSelectedEndDateTo] = useState<Date | null>(
    new Date(),
  );

  // just toasting
  const toaster = useToaster();

  // helper
  function buildFilter(settings: FirmaSlutDatoFilterSetting) {
    return f<FirmaDtoFilterInput>()
      .addFilter("periode", {
        or: settings.map((set) => set.filter),
      })
      .build();
  }

  function toastFilterExists() {
    toaster.push(
      <Notification>
        <div className="d-flex flex-row gap-3 align-items-center">
          <span className={`c-theme-icon text-light bg-warning`}>
            <WarningIcon />
          </span>
          Filteret er allerede tilføjet.
        </div>
      </Notification>,
      {
        placement: "bottomEnd",
        duration: 2500,
      },
    );
  }

  // handling handlers
  function updateFilterHandler() {
    const endDateSettings: FirmaSlutDatoFilterSetting = settings || [];

    // OK is disabled while the date is unfinished; the to-date is only
    // read when "between" is chosen (ADV-320).
    if (!selectedEndDateFrom) return;

    const selectedEndDateFromFormattedPretty = format(
      selectedEndDateFrom,
      "dd-MM-yyyy",
    );
    const selectedEndDateFromFormattedFilter = format(
      selectedEndDateFrom,
      "yyyy-MM-dd",
    );
    const selectedEndDateToFormattedPretty = format(
      selectedEndDateTo ?? selectedEndDateFrom,
      "dd-MM-yyyy",
    );
    const selectedEndDateToFormattedFilter = format(
      selectedEndDateTo ?? selectedEndDateFrom,
      "yyyy-MM-dd",
    );

    let endDateItem: FirmaSlutDatoFilterSetting[number];

    if (isEndDateBetween) {
      endDateItem = {
        key: `gt-${selectedEndDateFromFormattedPretty}-lt-${selectedEndDateToFormattedPretty}`,
        displayName: `${selectedEndDateFromFormattedPretty} - ${selectedEndDateToFormattedPretty}`,
        selectedQueryName: "Mellem",
        filter: {
          and: [
            {
              tilDatoInklusiv: {
                gt: selectedEndDateFromFormattedFilter,
              },
            },
            {
              tilDatoInklusiv: {
                lt: selectedEndDateToFormattedFilter,
              },
            },
          ],
        },
      };
    } else {
      endDateItem = {
        key: `${selectedEndDateQuery.query}-${selectedEndDateFromFormattedPretty}`,
        displayName: selectedEndDateFromFormattedPretty,
        selectedQueryName: selectedEndDateQuery.displayName,
        filter: {
          tilDatoInklusiv: {
            [selectedEndDateQuery.query]: selectedEndDateFromFormattedFilter,
          },
        },
      };
    }

    const filterExists = endDateSettings.some(
      (item) => item.key === endDateItem.key,
    );

    if (filterExists) {
      toastFilterExists();
    } else {
      endDateSettings.push(endDateItem);
    }

    const canBuildFilter = endDateSettings.length;
    const filterString = canBuildFilter ? buildFilter(endDateSettings) : "";

    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaSlutDatoFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: endDateSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(key: string) {
    const updatedEndDateSettings = settings?.filter((set) => set.key !== key);
    const canBuildFilter = updatedEndDateSettings?.length;

    if (canBuildFilter) {
      const filterString = canBuildFilter
        ? buildFilter(updatedEndDateSettings)
        : "";

      const queryFilter: QueryFilter<
        FirmaFilterKeys,
        FirmaSlutDatoFilterSetting
      > = {
        filter: filterString,
        filterKey: filterKey,
        settings: updatedEndDateSettings,
        parentFilter: "and",
      };

      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Ophørsdato"
      value="slutdato"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <div className="c-applied-filter-group">
        {settings?.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.selectedQueryName}`}
            label={`${set.displayName}`}
            key={`${set.key}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.key);
            }}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedEndDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsEndDateBetween(true);
              return;
            } else {
              setIsEndDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedEndDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedEndDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedEndDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isEndDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedEndDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedEndDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedEndDateFrom ||
            (isEndDateBetween &&
              (!selectedEndDateTo || selectedEndDateFrom > selectedEndDateTo))
          }
          onClick={() => {
            updateFilterHandler();
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
